import { useLayoutEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  styled,
} from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ACTIONS, ASSIGN_ROLES, Permission, ROLES, User } from '../../types/users';
import FormBuilder from '../controls/FormBuilder';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': { width: '80%', boxShadow: '0px 0px 55px 5px rgba(0,0,0,0.05)', borderRadius: '8px' },
  '& .MuiDialogContent-root': { padding: theme.spacing(2) },
  '& .MuiDialogActions-root': { padding: theme.spacing(1) },
}));

const initialPermission: Permission[] = [
  { resource: 'transactions', actions: [] },
  { resource: 'tasks', actions: [] },
  { resource: 'submissions', actions: [] },
  { resource: 'students', actions: [] },
  { resource: 'analytics', actions: [] },
  { resource: 'invoices', actions: [] },
];

const initialUserData: User = {
  username: '',
  role: '',
  email: '',
  tasks: [],
  submissions: [],
  transactions: [],
  assignRole: 'auto',
  permissions: initialPermission,
  _id: '',
};

function buildSchema() {
  return Yup.object({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    role: Yup.string().required('Role is required'),
    // Password is optional on both create and edit — on create the user sets it via email.
    password: Yup.string().min(8, 'Min 8 characters'),
  });
}

function UserForm({
  selectedUser,
  handleClose,
  open,
  edit,
  submitHanlder,
}: {
  selectedUser: User | null;
  handleClose: () => void;
  open: boolean;
  edit: boolean;
  submitHanlder: (user: User) => void;
}) {
  const [permissions, setPermissions] = useState<Permission[]>(initialPermission);

  useLayoutEffect(() => {
    if (selectedUser?.permissions) {
      setPermissions(
        initialPermission.map((p) => {
          const found = selectedUser.permissions.find((sp) => sp.resource === p.resource);
          return found ?? p;
        }),
      );
    }
    return () => setPermissions(initialPermission);
  }, [selectedUser]);

  const handlePermissionChange = (event: any, index: number) => {
    const { name, value } = event.target;
    setPermissions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const handleSubmit = (values: User) => {
    submitHanlder({ ...values, permissions });
  };

  const controls = useMemo(
    () => [
      {
        type: 'input',
        control: { additional: { fullWidth: true, required: true }, otherProps: { fullWidth: true }, id: 'username', name: 'username', label: 'Username' },
      },
      {
        type: 'select',
        control: { otherProps: { fullWidth: true }, additional: { fullWidth: true }, id: 'role', name: 'role', label: 'Role', options: ROLES.map((r) => ({ label: r, value: r })) },
      },
      {
        type: 'input',
        control: { additional: { fullWidth: true, required: true, type: 'email' }, otherProps: { fullWidth: true }, id: 'email', name: 'email', label: 'Email' },
      },
      {
        type: 'select',
        control: { otherProps: { fullWidth: true }, additional: { fullWidth: true }, id: 'assignRole', name: 'assignRole', label: 'Assign Role', options: ASSIGN_ROLES.map((r) => ({ label: r, value: r })) },
      },
      // Password only shown in edit mode — on create the user sets it via the invite email.
      ...(edit
        ? [
            {
              type: 'input',
              control: { additional: { fullWidth: true, type: 'password' }, otherProps: { fullWidth: true }, type: 'password', id: 'password', name: 'password', label: 'New Password (leave blank to keep)' },
            },
          ]
        : []),
    ],
    [edit],
  );

  return (
    <StyledDialog open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {edit ? `Edit ${selectedUser?.username ?? 'User'}` : 'Create New User'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}>
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Formik
          enableReinitialize
          initialValues={selectedUser ?? initialUserData}
          validationSchema={buildSchema()}
          onSubmit={handleSubmit}>
          {({ errors, touched }) => (
            <Box component={Form} id="user-form">
              <FormBuilder controls={controls} />

              {!edit && (
                <Alert icon={<MarkEmailRead fontSize="small" />} severity="info" sx={{ mt: 1.5 }}>
                  A <strong>Set Password</strong> email will be sent to the user automatically.
                </Alert>
              )}

              {/* Show Yup errors that FormBuilder doesn't surface */}
              {(['username', 'email', 'role', 'password'] as const).map((field) =>
                touched[field] && errors[field] ? (
                  <Typography key={field} variant="caption" color="error" display="block" mt={0.5}>
                    {errors[field] as string}
                  </Typography>
                ) : null,
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                Resource Permissions
              </Typography>
              <Grid container spacing={1.5}>
                {permissions.map((permission, index) => (
                  <Grid item xs={12} sm={6} key={permission.resource}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ textTransform: 'capitalize' }}>{permission.resource}</InputLabel>
                      <Select
                        name="actions"
                        multiple
                        value={permission.actions}
                        label={permission.resource}
                        onChange={(e) => handlePermissionChange(e, index)}
                        renderValue={(selected) => (selected as string[]).join(', ')}>
                        {Object.values(ACTIONS).map((action) => (
                          <MenuItem key={action} value={action}>
                            <Checkbox size="small" checked={permission.actions.includes(action)} />
                            <ListItemText primary={action} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Formik>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="submit" form="user-form" variant="contained">
          {edit ? 'Save Changes' : 'Create User'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default UserForm;
